// R4 (pure part) — anchor geometry. Given the anchor sidecar and each pane's
// *measured* block layout, this module synthesizes the implicit document
// start/end sentinel pairs and produces `SyncSegment[]`: per-segment pixel
// offsets in each pane. From those it derives (a) interpolated follower
// positions and (b) the bidirectional hard-alignment padding. Everything here
// is a pure function of its inputs — the DOM measurement and the scroll/layout
// side-effects live in the hooks (`use-sync-scroll`, `use-hard-align`), which
// keeps the alignment math unit-testable (functional-programming policy).
//
// SPEC "Sync anchors": anchors are a sidecar that relates two independent
// documents; alignment is COMPUTED at render time, never a stored merged grid.

import type { CorpusAnchorPair } from "@bindery/shared/corpus/anchors";
import type { PaneId, SyncSegment } from "@bindery/shared/corpus/reader";

/** A built segment list for a chapter pair (sentinels included). */
export type SyncSegmentList = SyncSegment[];

/**
 * One measured block in a pane's scroll content. Char span `[start, end)` is
 * into that pane's `DocumentModel.text`; `top`/`height` are pixels within the
 * scroll container's content box. Retelling blocks carry `id`; originals don't.
 */
export interface BlockBox {
  start: number;
  end: number;
  top: number;
  height: number;
  id?: string;
}

/** A pane's full measured geometry. */
export interface PaneMetrics {
  boxes: readonly BlockBox[];
  /** Total scrollable content height (px), used for the end sentinel. */
  contentHeight: number;
}

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

/**
 * Resolve a character offset into the original document to a pixel position:
 * the containing block's top plus a proportional slice of its height. Offsets
 * before the first / after the last block clamp to the nearest edge. Verse
 * originals measure one box per `verse-line` block (scripts/ingest/
 * verseLines.ts), so verse offsets resolve to real per-line geometry; an
 * offset that falls on an excluded newline between line spans snaps to the
 * next line's top.
 */
export function resolveOriginalPx(
  boxes: readonly BlockBox[],
  offset: number,
): number {
  if (boxes.length === 0) return 0;
  const first = boxes[0]!;
  const last = boxes[boxes.length - 1]!;
  if (offset <= first.start) return first.top;
  if (offset >= last.end) return last.top + last.height;
  for (const box of boxes) {
    if (offset < box.end) {
      const span = box.end - box.start;
      const frac = span > 0 ? clamp((offset - box.start) / span, 0, 1) : 0;
      return box.top + frac * box.height;
    }
  }
  return last.top + last.height;
}

/**
 * Resolve a retelling location (`blockId` + intra-block char offset) to a pixel
 * position. An unknown block id (stale anchor) resolves to 0 rather than
 * throwing — the reader degrades to whole-document interpolation.
 */
export function resolveRetellingPx(
  boxes: readonly BlockBox[],
  blockId: string,
  offset: number,
): number {
  const box = boxes.find((b) => b.id === blockId);
  if (!box) return 0;
  const span = box.end - box.start;
  const frac = span > 0 ? clamp(offset / span, 0, 1) : 0;
  return box.top + frac * box.height;
}

/** `null` when the anchor's retelling block id doesn't exist in this pane's
 *  measured boxes (a stale anchor) rather than silently resolving to 0. */
function anchorPx(
  pair: CorpusAnchorPair,
  retelling: PaneMetrics,
  original: PaneMetrics,
): Record<PaneId, number> | null {
  const knownBlock = retelling.boxes.some(
    (box) => box.id === pair.retelling.blockId,
  );
  if (!knownBlock) return null;
  return {
    retelling: resolveRetellingPx(
      retelling.boxes,
      pair.retelling.blockId,
      pair.retelling.offset,
    ),
    original: resolveOriginalPx(original.boxes, pair.originalOffset),
  };
}

/**
 * Build the interpolation segments for a chapter pair. The explicit anchors are
 * bracketed by the implicit **start** (`from: null`, px 0/0) and **end**
 * (`to: null`, px = each pane's content height) sentinels, so there are no
 * dead zones before the first or after the last anchor. With no explicit
 * anchors this yields a single whole-document segment (linear top-to-bottom) —
 * exactly the `--local` fallback until phase 4 seeds real anchors.
 *
 * Anchors that resolve to an unknown retelling block, or whose resolved px
 * doesn't strictly advance past the previous kept pair's in *both* panes, are
 * dropped — a stale or non-monotonic anchor would otherwise make
 * interpolation jump discontinuously instead of degrading gracefully.
 */
export function buildSyncSegments(
  anchors: readonly CorpusAnchorPair[],
  retelling: PaneMetrics,
  original: PaneMetrics,
): SyncSegment[] {
  const segments: SyncSegment[] = [];
  let fromPair: CorpusAnchorPair | null = null;
  let startPx: Record<PaneId, number> = { retelling: 0, original: 0 };

  for (const pair of anchors) {
    const endPx = anchorPx(pair, retelling, original);
    if (
      endPx === null ||
      endPx.retelling <= startPx.retelling ||
      endPx.original <= startPx.original
    ) {
      continue;
    }
    segments.push({ from: fromPair, to: pair, startPx, endPx });
    fromPair = pair;
    startPx = endPx;
  }

  segments.push({
    from: fromPair,
    to: null,
    startPx,
    endPx: {
      retelling: retelling.contentHeight,
      original: original.contentHeight,
    },
  });
  return segments;
}

/** The segment whose leader-pane px range contains `leaderPx` (clamped). */
function segmentAt(
  segments: readonly SyncSegment[],
  leader: PaneId,
  leaderPx: number,
): SyncSegment {
  for (const seg of segments) {
    if (leaderPx <= seg.endPx[leader]) return seg;
  }
  return segments[segments.length - 1]!;
}

/**
 * Interpolated sync: given the leader pane's position at the reading line
 * (`leaderPx`), return the follower pane's matching px by piecewise-linear
 * interpolation within the bracketing segment. A zero-height leader segment
 * (coincident anchors) resolves to the segment start rather than dividing by
 * zero.
 */
export function interpolateFollower(
  segments: readonly SyncSegment[],
  leader: PaneId,
  follower: PaneId,
  leaderPx: number,
): number {
  if (segments.length === 0) return 0;
  const seg = segmentAt(segments, leader, leaderPx);
  const leaderSpan = seg.endPx[leader] - seg.startPx[leader];
  const t =
    leaderSpan > 0
      ? clamp((leaderPx - seg.startPx[leader]) / leaderSpan, 0, 1)
      : 0;
  const followerSpan = seg.endPx[follower] - seg.startPx[follower];
  return seg.startPx[follower] + t * followerSpan;
}

/** Per-pane viewport parameters for scroll-space interpolation. */
export interface PaneScrollView {
  /** The reading line's offset within the viewport (clientHeight × ratio). */
  readingLine: number;
  /** Maximum scrollTop (scrollHeight − clientHeight, ≥ 0). */
  maxScrollTop: number;
}

/**
 * Interpolated sync in *scrollTop space*: map the leader pane's scrollTop to
 * the follower's. Interior anchors are converted from content px to the
 * scrollTop that puts them at the pane's reading line (`px − readingLine`), so
 * mid-document behavior matches `interpolateFollower` exactly — an anchor at
 * the leader's reading line puts its pair at the follower's reading line. The
 * endpoints, though, are pinned to the panes' *scroll ranges*: leader
 * scrollTop 0 maps to follower 0 and leader max to follower max, so scrolling
 * one pane fully to its top (or bottom) always brings the other pane fully to
 * its own top (bottom). The content-space sentinels can't provide that: the
 * reading line never reaches content px 0 (at scrollTop 0 it already sits
 * `readingLine` px in) nor `contentHeight` (which excludes the trailing scroll
 * padding), so whenever the boundary segments render at unequal ratios the
 * follower strands short of its extremes — the mockup's follower target merely
 * clamps negative at the top, which only masks the mismatch for ratios ≤ 1.
 * Anchors whose scroll-space position falls outside either pane's scrollable
 * range, or that don't strictly advance in both panes, are dropped — the same
 * graceful degradation as `buildSyncSegments`.
 */
export function followerScrollTop(
  segments: readonly SyncSegment[],
  leader: PaneId,
  follower: PaneId,
  leaderScrollTop: number,
  view: Record<PaneId, PaneScrollView>,
): number {
  const points: Record<PaneId, number>[] = [{ retelling: 0, original: 0 }];
  for (const seg of segments) {
    if (seg.to === null) continue; // end sentinel — replaced by the max point
    const point = {
      retelling: seg.endPx.retelling - view.retelling.readingLine,
      original: seg.endPx.original - view.original.readingLine,
    };
    const prev = points[points.length - 1]!;
    if (
      point.retelling <= prev.retelling ||
      point.original <= prev.original ||
      point.retelling >= view.retelling.maxScrollTop ||
      point.original >= view.original.maxScrollTop
    ) {
      continue;
    }
    points.push(point);
  }
  points.push({
    retelling: view.retelling.maxScrollTop,
    original: view.original.maxScrollTop,
  });

  let i = points.length - 1;
  for (let k = 1; k < points.length; k++) {
    if (leaderScrollTop <= points[k]![leader]) {
      i = k;
      break;
    }
  }
  const from = points[i - 1]!;
  const to = points[i]!;
  const leaderSpan = to[leader] - from[leader];
  const t =
    leaderSpan > 0
      ? clamp((leaderScrollTop - from[leader]) / leaderSpan, 0, 1)
      : 0;
  return clamp(
    from[follower] + t * (to[follower] - from[follower]),
    0,
    view[follower].maxScrollTop,
  );
}

/** Per-segment natural rendered height in a pane. */
function segHeight(seg: SyncSegment, pane: PaneId): number {
  return seg.endPx[pane] - seg.startPx[pane];
}

/**
 * Result of the hard-alignment pass: per-segment top padding (px) to inject at
 * each segment's END boundary on each pane, so both panes render every segment
 * at the same height and paired anchors sit at equal cumulative heights.
 */
export interface HardAlignPadding {
  retelling: number[];
  original: number[];
  /** The equalized total content height both panes share once padded. */
  totalHeight: number;
}

/**
 * Hard alignment (R5): equalize each segment's height across the two panes by
 * padding whichever side rendered SHORTER. Padding is bidirectional — although
 * the compressed retelling is usually the shorter side, at extreme font-size
 * ratios the original's rendered height can grow past it and the padded side
 * flips; this is decided per segment, so the flip is handled for free. Padding
 * for segment `i` is injected at that segment's end boundary (the next anchor's
 * containing block top, or a trailing spacer for the last segment) so the
 * segment's *start* anchor stays put while its *end* anchor is pushed down to
 * the shared height.
 */
export function computeHardAlignPadding(
  segments: readonly SyncSegment[],
): HardAlignPadding {
  const retelling: number[] = [];
  const original: number[] = [];
  let totalHeight = 0;
  for (const seg of segments) {
    const hR = segHeight(seg, "retelling");
    const hO = segHeight(seg, "original");
    const target = Math.max(hR, hO);
    retelling.push(target - hR);
    original.push(target - hO);
    totalHeight += target;
  }
  return { retelling, original, totalHeight };
}
